/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useCallback } from 'react';
import useNormalizedValue from '../../hooks/useNormalizedValue';
import { DoubleCalendarProps } from '../../types';
import { Calendar } from './Calendar';
import CalendarDaysOfWeek from './CalendarDayOfWeek';
import CalendarMonth from './CalendarMonth';
import {
  getFirstDayOfMonth,
  addMonth,
  nullFn,
  normalizeDate,
  getNextFocusedDate,
  getYearsList,
  addYear,
  getMonthList,
  setMonth,
} from '../../utils';
import useFormattedMonth from '../../hooks/useFormattedMonth';
import useYearsRange from '../../hooks/useYearsRange';
import useHeaderArrowNav from '../../hooks/useHeaderArrowNav';
import useDisabledControls from '../../hooks/useDisabledControls';
import LeftChevron from '../../../../../assets/images/svg/LeftChevron';
import RightChevron from '../../../../../assets/images/svg/RightChevron';
import CalendarHeaderYearSelector from './CalendarHeaderYearSelector';
import CalendarHeaderMonthSelector from './CalendarHeaderMonthSelector';

export default function DoubleCalendar({
  id = undefined,
  value = new Date(),
  locale = 'es-US',
  maxValue = undefined,
  minValue = undefined,
  onChange = nullFn,
  selectedRange = undefined,
  selectionType = 'single',
  className,
  style,
}: DoubleCalendarProps): JSX.Element {
  const currentValue = useNormalizedValue(value);
  const [focusedDate, setFocusedDate] = useState(currentValue);
  const [currentMonth, setCurrentMonth] = useState(
    getFirstDayOfMonth(currentValue),
  );
  const [currentRange, setCurrentRange] = useState(selectedRange);

  const rightCalMonth = addMonth(1, currentMonth);
  const currentYear = currentMonth.getFullYear();

  const currentMonthLabelFormatted = useFormattedMonth(currentMonth, locale);
  const rightMonthLableFormatted = useFormattedMonth(rightCalMonth, locale);
  const yearsRange = useYearsRange(currentMonth, minValue, maxValue);
  const [disablePrevMonth, disableNextMonth] = useDisabledControls(
    currentMonth,
    rightCalMonth,
    yearsRange,
    minValue,
    maxValue,
  );

  const refs = [
    React.useRef<HTMLButtonElement>(null),
    React.useRef<HTMLButtonElement>(null),
  ];

  const {
    updateFocusedItem,
    handleKeyDown: handleHeaderElementKeyDown,
  } = useHeaderArrowNav({
    disableNextMonth,
    disablePrevMonth,
    refs,
    firstIdx: disablePrevMonth ? 1 : 0,
    lastIdx: 2,
    delta: 1,
  });

  const yearList = getYearsList(currentYear);
  const monthList = getMonthList(currentYear, 'long', locale);

  const handleOnPrevMonthClick = useCallback(() => {
    const newMonth = addMonth(-1, currentMonth);
    setFocusedDate(getNextFocusedDate(value, newMonth));
    setCurrentMonth(newMonth);
    updateFocusedItem(0);
  }, [currentMonth, updateFocusedItem, value]);

  const handleOnNextMonthClick = useCallback(() => {
    const newMonth = addMonth(1, currentMonth);
    setFocusedDate(getNextFocusedDate(value, newMonth));
    setCurrentMonth(newMonth);
    updateFocusedItem(1);
  }, [currentMonth, updateFocusedItem, value]);

  const handleOnYearChange = useCallback(
    (year: number) => {
      const newYear = addYear(year, currentMonth);
      setFocusedDate(getNextFocusedDate(value, newYear));
      setCurrentMonth(newYear);
      updateFocusedItem(2);
    },
    [currentMonth, updateFocusedItem, value],
  );

  const handleOnRightYearChange = useCallback(
    (year: number) => {
      const adjYear =
        currentMonthLabelFormatted ===
        new Date(`December 01 2000`).toLocaleDateString(locale, {
          month: `long`,
        })
          ? year - 1
          : year;
      const newYear = addYear(adjYear, currentMonth);
      setFocusedDate(getNextFocusedDate(value, newYear));
      setCurrentMonth(newYear);
      updateFocusedItem(2);
    },
    [
      currentMonth,
      currentMonthLabelFormatted,
      locale,
      updateFocusedItem,
      value,
    ],
  );

  const handleOnMonthChange = useCallback(
    (month: number) => {
      const newMonth = setMonth(month, currentMonth);
      setFocusedDate(getNextFocusedDate(value, newMonth));
      setCurrentMonth(newMonth);
      updateFocusedItem(2);
    },
    [currentMonth, updateFocusedItem, value],
  );

  const handleOnRightMonthChange = useCallback(
    (month: number) => {
      const newMonth = setMonth(month, currentMonth);
      const adjMonth = new Date(newMonth.setMonth(newMonth.getMonth() - 1));
      setFocusedDate(getNextFocusedDate(value, adjMonth));
      setCurrentMonth(adjMonth);
      updateFocusedItem(2);
    },
    [currentMonth, updateFocusedItem, value],
  );

  useEffect(() => {
    setFocusedDate(currentValue);
  }, [currentValue]);

  useEffect(() => {
    setCurrentRange(selectedRange);
  }, [selectedRange]);

  return (
    <div id={id} className={className} style={style}>
      <div className='flex flex-row'>
        <div
          className='flex justify-between items-center flex-grow py-5 mx-auto'
          onKeyDown={handleHeaderElementKeyDown}
        >
          <button
            type='button'
            className='w-1/7 flex items-center justify-center outline-none focus:outline-none'
            onClick={handleOnPrevMonthClick}
            ref={refs[0]}
            onFocus={() => updateFocusedItem(0)}
            disabled={disablePrevMonth}
          >
            <LeftChevron />
          </button>
          <div className='flex flex-row flex-1 items-center justify-center space-x-2 select-none'>
            <CalendarHeaderMonthSelector
              formattedMonth={currentMonthLabelFormatted}
              monthList={monthList}
              handleOnClick={handleOnMonthChange}
            />
            <CalendarHeaderYearSelector
              currentYear={currentYear}
              yearsList={yearList}
              handleOnClick={handleOnYearChange}
            />
          </div>
        </div>
        <div
          className='flex justify-between items-center flex-grow py-5 mx-auto'
          onKeyDown={handleHeaderElementKeyDown}
        >
          <div className='flex flex-row flex-1 items-center justify-center space-x-2 select-none'>
            <CalendarHeaderMonthSelector
              formattedMonth={rightMonthLableFormatted}
              monthList={monthList}
              handleOnClick={handleOnRightMonthChange}
            />
            <CalendarHeaderYearSelector
              currentYear={
                currentMonthLabelFormatted ===
                new Date(`December 01 2000`).toLocaleDateString(locale, {
                  month: `long`,
                })
                  ? currentYear + 1
                  : currentYear
              }
              yearsList={yearList}
              handleOnClick={handleOnRightYearChange}
            />
          </div>
          <button
            type='button'
            className='w-1/7 flex items-center justify-center outline-none focus:outline-none'
            tabIndex={-1}
            onClick={handleOnNextMonthClick}
            ref={refs[3]}
            onFocus={() => updateFocusedItem(1)}
            disabled={disableNextMonth}
          >
            <RightChevron />
          </button>
        </div>
      </div>
      <div className='flex flex-row items-start space-x-10'>
        <section>
          <table
            className='border-collapse w-full box-border table table-fixed'
            cellSpacing='0'
          >
            <CalendarDaysOfWeek locale={locale} />
            <Calendar
              selectionType={selectionType}
              focusedDate={normalizeDate(focusedDate)}
              currentRange={currentRange}
              selectedRange={selectedRange}
            >
              <CalendarMonth
                value={value}
                firstOfMonth={currentMonth}
                minValue={minValue}
                maxValue={maxValue}
                onChange={onChange}
              />
            </Calendar>
          </table>
        </section>
        <section>
          <table
            className='border-collapse w-full box-border table table-fixed'
            cellSpacing='0'
          >
            <CalendarDaysOfWeek locale={locale} />
            <Calendar
              selectionType={selectionType}
              focusedDate={normalizeDate(focusedDate)}
              currentRange={currentRange}
              selectedRange={selectedRange}
            >
              <CalendarMonth
                value={value}
                firstOfMonth={rightCalMonth}
                minValue={minValue}
                maxValue={maxValue}
                onChange={onChange}
              />
            </Calendar>
          </table>
        </section>
      </div>
    </div>
  );
}
